import { useNavigate, useParams } from "react-router";
import CDS from "design-system";
import AppPage from "layouts/AppPage";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  VStack,
  Box,
  Flex,
  Image,
  HStack,
  Card,
  CardBody,
  Divider,
  Switch,
} from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useNavbar } from "hooks/useNavbar";
import { titleize } from "helpers/string";
import { DeleteIcon } from "@chakra-ui/icons";
import { webhookUrl } from "helpers/integrations";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import CardFieldRow from "components/DataDisplay/CardFieldRow";
import { timeAtDayMonth } from "helpers/time";
import { useDeleteIntegration, useIntegrationDetailQuery, useToggleActiveIntegration, useUpdateCalendarService } from "features/Integrations/hooks";
import { getIntegrationLogoUrl } from "models/integration";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { Heading20 } from "components/Heading";
import { TertiaryButton } from "components/Button";
import SyncUsersButton from "./CompanyIntegrationsPage/SyncUsersButton";
import SyncChannelsButton from "./CompanyIntegrationsPage/SyncChannelsButton";

const CONFIRM_DELETE = {
  header: "CONFIRM you want to delete this integration",
  body: "This cannot be undone! Be sure you want to remove this integration now.",
  confirmButtonText: "Confirm Delete",
};

export default function IntegrationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = useNavbar();

  const { data } = useIntegrationDetailQuery(id || "");

  const { performRequest: toggleIsActive } = useToggleActiveIntegration(id || "", data?.isActive ?? false);

  const handleToggle = useCallback(() => {
    toggleIsActive();
  }, [toggleIsActive]);

  useEffect(() => {
    if (data) {
      setPageTitle(`${titleize(data?.partner)} Integration`);
    }
  }, [data, setPageTitle]);

  const { performRequest: handleDelete } = useDeleteIntegration(
    id || "",
    () => navigate(PEOPLE_TEAM_ROUTES.company.integrations.index),
  );

  const { mutate: updateCalendarService } = useUpdateCalendarService(
    data?.calendarService?.id || "",
    id || ""
  );

  const handleToggleBuiltInVc = useCallback(() => {
    if (data?.calendarService) {
      updateCalendarService({ useBuiltInVcService: !data.calendarService.useBuiltInVcService });
    }
  }, [data, updateCalendarService]);

  if (!data) {
    return <AppPage>Loading...</AppPage>;
  }

  const isActive = data.isActive;
  const isPauseable = !["slack", "google_calendar", "zoom"].includes(data.partner);
  const logoUrl = getIntegrationLogoUrl(data.partner);

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[
          { name: "Integrations", toUrl: PEOPLE_TEAM_ROUTES.company.integrations.index },
          { name: titleize(data.partner) },
        ]}
      />
      <Box maxWidth="800px" mx="auto" py={8}>
        <Card>
          <CardBody p={8}>
            <VStack align="stretch" spacing={0}>
              {data.needsReauth && (
                <Alert status="error" mb={6} borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Reconnection required</AlertTitle>
                    <AlertDescription>
                      Authorization has expired or been revoked. Re-authorize this integration to restore it.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
              <Flex alignItems="center" justifyContent="space-between" mb={8}>
                <HStack spacing={4}>
                  {logoUrl && (
                    <Image
                      src={logoUrl}
                      alt={data.partner}
                      w="32px"
                      h="32px"
                      objectFit="contain"
                      borderRadius="md"
                    />
                  )}
                  <Heading20>
                    {titleize(data.partner)}
                  </Heading20>
                </HStack>
                <HStack spacing={3}>
                  <CDS.Tag
                    size={CDS.Tag.size.large}
                    variant={isActive ? CDS.Tag.variant.success : CDS.Tag.variant.warning}
                  >
                    {isActive ? "Active" : "Paused"}
                  </CDS.Tag>
                  {isPauseable && (
                    <Switch
                      colorScheme="brand"
                      isChecked={isActive}
                      onChange={handleToggle}
                      size="sm"
                    />
                  )}
                </HStack>
              </Flex>

              <VStack align="stretch" spacing={6} mb={8}>
                <Flex alignItems="center">
                  <CDS.Text12 color="fg.muted" minWidth="160px">
                    Added:
                  </CDS.Text12>
                  <CDS.Text12 flex={1}>
                    {timeAtDayMonth(data.createdAt)}
                  </CDS.Text12>
                </Flex>

                <Divider />

                <Flex alignItems="center">
                  <CDS.Text12 color="fg.muted" minWidth="160px">
                    Last updated:
                  </CDS.Text12>
                  <CDS.Text12 flex={1}>
                    {timeAtDayMonth(data.updatedAt)}
                  </CDS.Text12>
                </Flex>

                {data.connectionType === "webhook" && (
                  <>
                    <Divider />
                    <CardFieldRow
                      fieldName="Webhook URL"
                      displayValue={webhookUrl(data)}
                      copyableValue={webhookUrl(data)}
                      flexDirection="column"
                      fontSize="sm"
                      headerProps={{
                        fontWeight: "normal",
                        color: "fg.muted",
                        fontSize: "sm",
                      }}
                    />
                  </>
                )}

                {data.partner === "slack" && data.messageService && (
                  <>
                    <Divider />

                    <Flex alignItems="center">
                      <CDS.Text12 color="fg.muted" minWidth="160px">
                        Slack Work space:
                      </CDS.Text12>
                      <CDS.Text12 fontWeight="medium" flex={1}>
                        {data.messageService.url}
                      </CDS.Text12>
                      <Box>
                        <TertiaryButton
                          size="sm"
                          height="100%"
                          px={6}
                          py={1}
                          onClick={() => {
                            window.open(data.messageService?.authUrl, "_blank", "noopener,noreferrer");
                          }}
                        >
                          Re-authorize
                        </TertiaryButton>
                      </Box>
                    </Flex>

                    <Divider />

                    <Flex alignItems="center">
                      <CDS.Text12 color="fg.muted" minWidth="160px">
                        Channels last synced:
                      </CDS.Text12>
                      <CDS.Text12 flex={1}>
                        {data.messageService.channelsLastSyncedAt
                          ? timeAtDayMonth(new Date(data.messageService.channelsLastSyncedAt))
                          : "Never"}
                      </CDS.Text12>
                      <Box>
                        <SyncChannelsButton />
                      </Box>
                    </Flex>

                    <Divider />

                    <Flex alignItems="center">
                      <CDS.Text12 color="fg.muted" minWidth="160px">
                        Users last synced:
                      </CDS.Text12>
                      <CDS.Text12 flex={1}>
                        {data.messageService.usersLastSyncedAt
                          ? timeAtDayMonth(new Date(data.messageService.usersLastSyncedAt))
                          : "Never"}
                      </CDS.Text12>
                      <Box>
                        <SyncUsersButton />
                      </Box>
                    </Flex>
                  </>
                )}

                {data.partner === "google_calendar" && data.calendarService && (
                  <>
                    {data.calendarService.authUrl && (
                      <>
                        <Divider />

                        <Flex alignItems="center">
                          <CDS.Text12 color="fg.muted" minWidth="160px">
                            Authorization:
                          </CDS.Text12>
                          <CDS.Text12 flex={1}>
                            Reconnect to refresh Google Calendar access
                          </CDS.Text12>
                          <Box>
                            <TertiaryButton
                              size="sm"
                              height="100%"
                              px={6}
                              py={1}
                              onClick={() => {
                                window.open(data.calendarService?.authUrl, "_blank", "noopener,noreferrer");
                              }}
                            >
                              Re-authorize
                            </TertiaryButton>
                          </Box>
                        </Flex>
                      </>
                    )}

                    <Divider />

                    <Flex alignItems="center">
                      <CDS.Text12 color="fg.muted" minWidth="160px">
                        Use Google Meet:
                      </CDS.Text12>
                      <CDS.Text12 flex={1}>
                        Automatically add Google Meet video conferencing to meetings
                      </CDS.Text12>
                      <Box>
                        <Switch
                          colorScheme="brand"
                          isChecked={data.calendarService.useBuiltInVcService}
                          onChange={() => handleToggleBuiltInVc()}
                          size="md"
                        />
                      </Box>
                    </Flex>

                    <Divider />

                    <Flex alignItems="center">
                      <CDS.Text12 color="fg.muted" minWidth="160px">
                        Calendar Management:
                      </CDS.Text12>
                      <CDS.Text12 flex={1}>
                        Manage your Google Calendar settings and permissions
                      </CDS.Text12>
                      <Box>
                        <TertiaryButton
                          size="sm"
                          height="100%"
                          px={6}
                          py={1}
                          onClick={() => navigate(PEOPLE_TEAM_ROUTES.company.calendarSettings)}
                        >
                          Calendar Settings
                        </TertiaryButton>
                      </Box>
                    </Flex>
                  </>
                )}
              </VStack>

              <Divider mb={6} />

              <Flex justifyContent="flex-end">
                <ButtonWithConfirm {...CONFIRM_DELETE} handleConfirm={handleDelete}>
                  <CDS.Button
                    leftIcon={{ customIcon: <DeleteIcon /> }}
                    intent={CDS.Button.intent.error}
                    size={CDS.Button.size.md}
                  >
                    Delete Integration
                  </CDS.Button>
                </ButtonWithConfirm>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    </AppPage>
  );
}
